import { Switch, TextField } from '@mui/material';

import { EntityMetadataModel, EnumMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import { ParameterTypes } from '@@common';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ExpressionPlaceholder from '../Expressions/ExpressionPlaceholder';
import EnumerationInput from '../Inputs/EnumerationInput';
import ContentTemplateInput from '../Inputs/ContentTemplateInput';
import IntegrationEventInput from '../Inputs/IntegrationEventInput';
import OperationInput from '../Inputs/OperationInput';
import SchemaInput from '../Inputs/SchemaInput';
import EditableTextField from './EditableTextField';
import VarPathField from './VarPathField';

type ParameterPlaceholderProps = {
	value: any | undefined;
	onValueChange?: (value: any | undefined) => void;
	parameterMetadata: ParameterMetadata;
	parameterEnum?: EnumMetadataModel; // when set, forwards enum to const input inside ExpressionPlaceholder
	variableScope?: IVariableScope; // Input scope: variables available when editing parameters
	isEditable?: boolean;
	contextMenuId?: string; // shared context menu ID for nested expressions
	expectedObjectType?: EntityMetadataModel; // resolved object schema when typeRef points to a ref parameter
	model?: Record<string, any>;
	fullWidth?: boolean;
};

export default function ParameterPlaceholder({
	value,
	onValueChange,
	parameterMetadata,
	parameterEnum,
	variableScope,
	isEditable,
	contextMenuId,
	expectedObjectType,
	model,
	fullWidth,
}: ParameterPlaceholderProps) {
	if (parameterMetadata.type === ParameterType.Expression) {
		return (
			<ExpressionPlaceholder
				expression={value as ExpressionModel}
				onExpressionChange={onValueChange}
				parameterMetadata={parameterMetadata}
				parameterEnum={parameterEnum}
				variableScope={variableScope}
				isEditable={isEditable}
				contextMenuId={contextMenuId}
				expectedObjectType={expectedObjectType}
				model={model}
			/>
		);
	}

	if (parameterMetadata.type === ParameterType.OperationRef) {
		return <OperationInput operationRef={value} onOperationRefChange={(v) => onValueChange?.(v)} isEditable={isEditable} />;
	}

	if (parameterMetadata.type === ParameterType.SchemaRef) {
		return <SchemaInput schemaRef={value} onSchemaRefChange={(v) => onValueChange?.(v)} isEditable={isEditable} />;
	}

	if (parameterMetadata.type === ParameterType.IntegrationEventRef) {
		return <IntegrationEventInput integrationEventRef={value} onIntegrationEventRefChange={(v) => onValueChange?.(v)} isEditable={isEditable} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.EnumRef) {
		return <EnumerationInput enumRef={value} onEnumRefChange={(v) => onValueChange?.(v)} isEditable={isEditable} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.ContentTemplateId) {
		return <ContentTemplateInput templateId={value as string | undefined} onTemplateIdChange={(v) => onValueChange?.(v)} isEditable={isEditable} />;
	}

	if (parameterMetadata.type === ParameterType.Primitive) {
		if (parameterMetadata.expectedType?.type === PrimitiveType.String) {
			if (parameterMetadata.isCollection) {
				// For VarPathField, use variableScope directly
				// If undefined, VarPathField will show "no variables available"
				return <VarPathField value={value as string[]} onValueChange={onValueChange} variableScope={variableScope} />;
			}
			return <EditableTextField label={parameterMetadata.name} value={value as string} onValueChanged={onValueChange} isEditable={isEditable} fullWidth={fullWidth} />;
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.Boolean) {
			return (
				<Switch
					checked={value === true}
					onChange={(e) => onValueChange?.(e.target.checked)}
					onClick={isEditable ? (e) => e.stopPropagation() : undefined}
					disabled={!isEditable}
					size="small"
				/>
			);
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.Integer) {
			return (
				<TextField
					type="number"
					value={value ?? ''}
					onChange={(e) => onValueChange?.(e.target.value ? parseInt(e.target.value, 10) : undefined)}
					onClick={isEditable ? (e) => e.stopPropagation() : undefined}
					disabled={!isEditable}
					size="small"
					variant="outlined"
					slotProps={{ htmlInput: { step: 1 } }}
					sx={{ minWidth: '100px' }}
				/>
			);
		}
	}

	return <span>ToDo_Param</span>;
}
